import type {
  ModuleAudienceEnum,
  ModuleCatalogItem,
  ModuleConfigItemLowerOverrideCounts,
  ModuleOverrideInfo,
  ModuleScopeEnum,
} from '@/api/model'

/** Where an effective setting comes from (TR-1200 R12 source-of-setting). */
export const SOURCE_LABELS: Record<ModuleScopeEnum, string> = {
  global: 'z globalnego',
  school: 'ze szkoły',
  class: 'z klasy',
  student: 'nadpisanie indywidualne',
  user: 'nadpisanie indywidualne',
}

/** Polish 1/2-4/5+ plural pick (12–14 always take the "many" form). */
export function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one
  const tens = n % 100
  const units = n % 10
  if (units >= 2 && units <= 4 && (tens < 12 || tens > 14)) return few
  return many
}

const COUNT_NOUNS: Record<string, [string, string, string]> = {
  schools: ['szkoła', 'szkoły', 'szkół'],
  classes: ['klasa', 'klasy', 'klas'],
  students: ['uczeń', 'uczniów', 'uczniów'],
  users: ['użytkownik', 'użytkowników', 'użytkowników'],
}

/**
 * "2 szkoły, 1 klasa, 5 uczniów" — the lower-level overrides a higher-level
 * change will NOT affect (TR-1200, clarified counts). Empty string when
 * nothing is overridden below.
 */
export function countsSummary(counts?: ModuleConfigItemLowerOverrideCounts): string {
  if (!counts) return ''
  return Object.entries(COUNT_NOUNS)
    .filter(([key]) => (counts[key] ?? 0) > 0)
    .map(([key, [one, few, many]]) => `${counts[key]} ${plural(counts[key], one, few, many)}`)
    .join(', ')
}

/** Copy for the non-toggleable rows (modules I and IV — TR-1200 R1/R2). */
export function alwaysOnDescription(
  item: Pick<ModuleCatalogItem, 'always_on_rule'>,
  audience: ModuleAudienceEnum,
): string {
  if (item.always_on_rule === 'always') return 'Zawsze włączony dla wszystkich użytkowników.'
  if (audience === 'school') return 'Zawsze włączony dla uczniów szkolnych.'
  return 'Nieaktywny dla użytkowników pozaszkolnych.'
}

/** True when the always-on module is actually active in the given tree. */
export function alwaysOnActive(
  item: Pick<ModuleCatalogItem, 'always_on_rule'>,
  audience: ModuleAudienceEnum,
): boolean {
  return item.always_on_rule === 'always' || audience === 'school'
}

/** "11.07.2026, 19:30 — admin@example.com" or the seeded-default note (R12). */
export function changeMeta(override: ModuleOverrideInfo | null): string {
  if (!override) return ''
  const when = new Date(override.updated_at).toLocaleString('pl-PL', {
    dateStyle: 'short',
    timeStyle: 'short',
  })
  if (!override.updated_by) return 'ustawienie początkowe'
  return `${when} — ${override.updated_by.email}`
}

/**
 * Old → new state pair for an audit row (TR-1200 change history). The audit
 * endpoint carries `old_enabled`/`new_enabled` where null means "no explicit
 * override at that point": `null → x` is a first override (the previous state was
 * inherited), `x → null` is a removal (the override was deleted, back to
 * inheritance). Both-null never happens for a logged change, but it is guarded so
 * the caller never renders an empty cell.
 */
export function auditStateLabel(
  oldEnabled: boolean | null | undefined,
  newEnabled: boolean | null | undefined,
): { from: string; to: string } {
  const label = (value: boolean | null | undefined, absent: string): string => {
    if (value == null) return absent
    return value ? 'Włączony' : 'Wyłączony'
  }
  return {
    from: label(oldEnabled, 'dziedziczone'),
    to: label(newEnabled, 'usunięte'),
  }
}
